<template>
  <BaseCard :heading="$t('package.pricing_heading')">
    <Flex direction="column">
      <BaseRadio
        v-model="priceMode"
        :options="[
          {
            value: 'service_prices',
            label: $t('package.service_prices')
          },
          {
            value: 'fixed',
            label: $t('package.price')
          },
          {
            value: 'percentage',
            label: $t('package.percentage')
          }
        ]"
        v-test="'package-price-mode'"
      />
      <BaseInput
        v-if="priceMode === 'fixed'"
        v-model="fixedPrice"
        :label="$t('package.price')"
        type="currency"
        required
        v-test="'package-fixed-price'"
      />
      <BaseInput
        v-if="priceMode === 'percentage'"
        v-model="discountPercentage"
        :label="$t('package.percentage')"
        type="number"
        unitLabel="percentage"
        required
        :minValue="0"
        :maxValue="100"
        v-test="'package-discount-percentage'"
      />
      <template v-if="rows.length">
        <BaseTable
          :headers="[
            $t('global.items.service', 1),
            $t('global.vat'),
            $t('global.price'),
            $t('package.price')
          ]"
          :rows="rows"
          :footers="footerRow"
          v-test="'package-pricing-table'"
        />
        <BaseAlert
          v-if="totalSavings > 0"
          color="success"
          :text="$t('package.savings', { amount: filters.currency(totalSavings), percentage: (totalSavings / totalOriginal * 100).toFixed(0) })"
          v-test="'package-savings'"
        />
      </template>
    </Flex>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import { useServiceGroupsStore } from '@/stores/service-groups';
import { useVatRatesStore } from '@/stores/vat-rates';
import { computeLinePrices } from './pricing';
import { formatServiceLabel } from './serviceLabel';
import type { PackagePriceMode, PackageServiceItem } from './types';

const { t } = useI18n();

const props = defineProps<{
  items: PackageServiceItem[];
}>();

const priceMode = defineModel<PackagePriceMode>('priceMode', {
  required: true,
});
const fixedPrice = defineModel<number | null>('fixedPrice', { required: true });
const discountPercentage = defineModel<number | null>('discountPercentage', {
  required: true,
});

const { serviceGroups } = storeToRefs(useServiceGroupsStore());
const { vatRates } = storeToRefs(useVatRatesStore());

const serviceById = computed(() => {
  const map = new Map<number, { name: string; vatRateId: number | null }>();
  serviceGroups.value.forEach((group) => {
    group.services.forEach((service) => {
      map.set(service.id, {
        name: formatServiceLabel(group, service),
        vatRateId: service.vatRateId ?? null,
      });
    });
  });
  return map;
});

const vatPercentageFor = (vatRateId: number | null) => {
  const rate = vatRateId
    ? vatRates.value.find((r) => r.id === vatRateId)
    : vatRates.value.find((r) => r.default);
  return rate?.percentage ?? 0;
};

const selectedItems = computed(() =>
  props.items.filter(
    (item) => !item.destroy && item.serviceId && item.originalPrice != null,
  ),
);

const linePrices = computed(() =>
  computeLinePrices(
    selectedItems.value.map((item) => item.originalPrice as number),
    priceMode.value,
    {
      fixedPrice: fixedPrice.value || 0,
      discountPercentage: discountPercentage.value || 0,
    },
  ),
);

const rows = computed(() =>
  selectedItems.value.map((item, index) => {
    const service = serviceById.value.get(item.serviceId as number);
    const originalPrice = item.originalPrice as number;
    const packagePrice = linePrices.value[index];
    const originalPriceText = filters.currency(originalPrice);

    return {
      id: item._uid,
      cells: [
        { value: service?.name ?? '', fullWidth: true },
        {
          value: `${vatPercentageFor(service?.vatRateId ?? null)}%`,
          alignRight: true,
        },
        {
          value: originalPriceText,
          strikethrough: packagePrice !== originalPrice,
          alignRight: true,
        },
        { value: filters.currency(packagePrice), alignRight: true },
      ],
    };
  }),
);

const totalOriginal = computed(() =>
  selectedItems.value.reduce(
    (sum, item) => sum + (item.originalPrice as number),
    0,
  ),
);

const totalPackagePrice = computed(() =>
  linePrices.value.reduce((sum, price) => sum + price, 0),
);

const totalSavings = computed(
  () => totalOriginal.value - totalPackagePrice.value,
);

// Fixed package price defaults to the sum of the included services' prices.
// Packages saved in another price mode store fixedPrice as 0, not null, so
// treat that the same as unset here.
watch(priceMode, (mode) => {
  if (mode === 'fixed' && !fixedPrice.value) {
    fixedPrice.value = totalOriginal.value;
  }
});

const footerRow = computed(() => {
  const totalOriginalText = filters.currency(totalOriginal.value);

  return [
    { value: t('package.table_total'), fullWidth: true },
    { value: '', alignRight: true },
    {
      value: totalOriginalText,
      strikethrough: totalSavings.value > 0,
      alignRight: true,
    },
    { value: filters.currency(totalPackagePrice.value), alignRight: true },
  ];
});
</script>
